"use client"

import { getSiteUrl } from "@repo/shared"
import { useLocale, useTranslations } from "next-intl"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Drawer, Tooltip } from "antd"

import styles from "./index.module.scss"

interface MetrixDrawerEntryProps {
  fixed?: boolean
}

const LITE_CHAT_ACTION_EVENT = "metrix-lite-chat-action"
const LITE_CHAT_ACTION_NEW_CHAT = "new_chat"
const LITE_CHAT_ACTION_HISTORY = "history"
const LITE_CHAT_ACTION_OPEN = "open"
const LITE_CHAT_ACTION_CLOSE = "close"
const LITE_CHAT_ACTION_SEND = "inputsend"
const LITE_CHAT_ACTION_SIGN_IN = "sign_in"
const LITE_CHAT_ACTION_SIGN_IN_SUCCESS = "sign_in_success"
// Temporary locale Lite target for host-side integration.
// const LITE_CHAT_ORIGIN = "https://testlocal.metal.com:5006"
const LITE_CHAT_ORIGIN = getSiteUrl("ai")
const LITE_CHAT_SRC = `${LITE_CHAT_ORIGIN}/lite`
const METRIX_DRAWER_OPENED_BEFORE_KEY = "metrixDrawerOpenedBefore"
const METRIX_DRAWER_REOPEN_AFTER_SIGN_IN_KEY = "metrixDrawerReopenAfterSignIn"
// const LITE_CHAT_ORIGIN = getSiteUrl("ai")

export default function MetrixDrawerEntry({ fixed = false }: MetrixDrawerEntryProps) {
  const locale = useLocale()
  const t = useTranslations("MetrixDrawerEntry")
  const liteChatSrc = useMemo(() => {
    const url = new URL(`${LITE_CHAT_ORIGIN}/lite`)
    url.searchParams.set("locale", locale)
    return url.toString()
  }, [locale])
  const [open, setOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isEntryCollapsed, setIsEntryCollapsed] = useState(false)
  const [openedBefore, setOpenedBefore] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const markDrawerOpenedBefore = useCallback(() => {
    localStorage.setItem(METRIX_DRAWER_OPENED_BEFORE_KEY, "true")
    setOpenedBefore(true)
  }, [])

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 720px)")
    const updateIsMobile = () => setIsMobile(mediaQuery.matches)
    const shouldReopenAfterSignIn = localStorage.getItem(METRIX_DRAWER_REOPEN_AFTER_SIGN_IN_KEY) === "true"

    setOpenedBefore(localStorage.getItem(METRIX_DRAWER_OPENED_BEFORE_KEY) === "true" || shouldReopenAfterSignIn)

    if (shouldReopenAfterSignIn) {
      localStorage.removeItem(METRIX_DRAWER_REOPEN_AFTER_SIGN_IN_KEY)
      localStorage.setItem(METRIX_DRAWER_OPENED_BEFORE_KEY, "true")
      setOpen(true)
    }

    updateIsMobile()
    mediaQuery.addEventListener("change", updateIsMobile)

    return () => {
      mediaQuery.removeEventListener("change", updateIsMobile)
    }
  }, [])

  const handleLiteSignIn = useCallback(
    (payload?: { trigger?: string; storageKey?: string; storageValue?: string }) => {
      const handleSignInSuccess = () => {
        if (payload?.trigger === "data_pro") {
          iframeRef.current?.contentWindow?.postMessage(
            {
              source: LITE_CHAT_ACTION_EVENT,
              action: LITE_CHAT_ACTION_SIGN_IN_SUCCESS,
            },
            LITE_CHAT_ORIGIN
          )
        }

        localStorage.setItem(METRIX_DRAWER_REOPEN_AFTER_SIGN_IN_KEY, "true")
        markDrawerOpenedBefore()
        window.location.reload()
      }

      window.SmmAuth?.showLogin({
        action: "login",
        language: locale,
        onLoginSuccess: handleSignInSuccess,
        onRegisterSuccess: handleSignInSuccess,
      })
    },
    [locale, markDrawerOpenedBefore]
  )

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      // 安全校验：只接受来自 ai.metal.com 的消息
      if (event.origin !== LITE_CHAT_ORIGIN) return

      const message = event.data
      const isObjectMessage = typeof message === "object" && message !== null
      const source = isObjectMessage ? message.source : undefined
      const action = isObjectMessage ? message.action : message
      const payload = isObjectMessage ? message.payload : undefined

      if (source !== LITE_CHAT_ACTION_EVENT) {
        return
      }

      console.log("Received message from Metrix Lite Chat:", event)

      switch (action) {
        case LITE_CHAT_ACTION_NEW_CHAT:
          // 在新标签打开完整工作区
          window.open(getSiteUrl("ai"), "_blank")
          break

        case LITE_CHAT_ACTION_HISTORY: {
          // 跳转到宿主的历史页面，或在宿主侧显示历史面板
          const { conversationId } = payload ?? {}
          window.open(`${LITE_CHAT_ORIGIN}/conversations?open_sidebar=1&conversation_id=${conversationId}`, "_blank")
          break
        }
        case LITE_CHAT_ACTION_OPEN:
          // 在新标签打开完整工作区
          window.open(getSiteUrl("ai"), "_blank")
          break
        case LITE_CHAT_ACTION_CLOSE:
          // 关闭/隐藏 iframe 容器
          setOpen(false)
          break
        case LITE_CHAT_ACTION_SEND:
          console.log("User sent a message in Metrix Lite Chat:", payload)
          markDrawerOpenedBefore()
          break
        case LITE_CHAT_ACTION_SIGN_IN:
          handleLiteSignIn(payload)
          break
        default:
          console.warn("Unknown action from Metrix Lite Chat:", action)
      }
    }

    window.addEventListener("message", listener)

    return () => {
      window.removeEventListener("message", listener)
    }
  }, [handleLiteSignIn, markDrawerOpenedBefore])

  useEffect(() => {
    if (!isMobile) {
      setIsEntryCollapsed(false)
      return
    }

    const handleScroll = () => {
      setIsEntryCollapsed(window.scrollY > 20)
    }

    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [isMobile])

  const openDrawer = useCallback(() => {
    setOpen(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  const handleEntryClick = useCallback(() => {
    if (localStorage.getItem(METRIX_DRAWER_OPENED_BEFORE_KEY) === "true") {
      setOpenedBefore(true)
    }
    markDrawerOpenedBefore()
    openDrawer()
  }, [markDrawerOpenedBefore, openDrawer])

  return (
    <>
      <div className={fixed ? styles.fixedEntry : undefined}>
        <Tooltip
          styles={{
            root: {
              maxWidth: "none", // 取消默认250px最大宽度
              width: "fit-content", // 宽度自适应内容
            },
          }}
          trigger={"hover"}
          title={isMobile ? "" : t("entryTooltip")}
          placement="topLeft"
        >
          <Button
            className={`${styles.entryButton} ${isMobile && isEntryCollapsed ? styles.entryButtonCollapsed : ""}`}
            onClick={handleEntryClick}
            shape="round"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="icon">
                <path
                  id="Rectangle 34625197"
                  d="M2 2.7002H18C18.718 2.7002 19.2998 3.28203 19.2998 4V14C19.2998 14.718 18.718 15.2998 18 15.2998H5.70117C5.08822 15.2999 4.49329 15.5087 4.01465 15.8916L0.700195 18.543V4C0.700195 3.28203 1.28203 2.7002 2 2.7002Z"
                  stroke="white"
                  strokeWidth="1.4"
                />
                <circle id="Ellipse 1178" cx="6" cy="9" r="1" fill="white" />
                <circle id="Ellipse 1179" cx="10" cy="9" r="1" fill="white" />
                <circle id="Ellipse 1180" cx="14" cy="9" r="1" fill="white" />
              </g>
            </svg>
            <span className={styles.entryButtonText}>Ask Metrix</span>
          </Button>
        </Tooltip>
      </div>
      <Drawer
        className={`${styles.drawer} ${isMobile ? styles.mobileDrawer : ""}`}
        open={open}
        onClose={closeDrawer}
        placement={isMobile ? "bottom" : "right"}
        width={isMobile ? undefined : "min(480px, 100vw)"}
        height={isMobile ? (openedBefore ? "calc(100% - 48px)" : "calc(100% - 208px)") : undefined}
        closable={false}
        // destroyOnHidden
        mask={isMobile}
      >
        {/* <div className={styles.header}>
          <div className={styles.brand}>
            <Image
              src={"https://imgqn.smm.cn/static/image/metal/ai-logo.svg"}
              alt="Metrix Logo"
              width={32}
              height={32}
            />
            <span className={styles.brandName}>Metrix</span>
          </div>
          <div className={styles.actions}>
            <Tooltip title="New chat" placement="bottom">
              <Button className={styles.iconAction} type="text" aria-label="New chat" onClick={handlePlaceholderClick}>
                <IconFont type="icon-kaiqixinduihua" style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
            <Tooltip title="Looking for past conversations? Click here" placement="bottom">
              <Button
                className={styles.iconAction}
                type="text"
                aria-label="Looking for past conversations? Click here"
                onClick={handlePlaceholderClick}
              >
                <IconFont type="icon-lishijilu" style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
            <Tooltip title="New tab" placement="bottom">
              <Button className={styles.iconAction} type="text" aria-label="New tab" onClick={handlePlaceholderClick}>
                <IconFont type="icon-dakaixinyemian" style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
            <Button
              className={styles.closeButton}
              type="text"
              icon={
                <CloseOutlined
                  style={{
                    fontSize: 18,
                  }}
                />
              }
              aria-label="Close Metrix"
              onClick={() => setOpen(false)}
            />
          </div>
        </div> */}
        <iframe ref={iframeRef} allow="clipboard-write" className={styles.iframe} src={liteChatSrc} title="Metrix" />
      </Drawer>
    </>
  )
}
